import { useEffect, useMemo, useState, type FormEvent } from "react";
import { TodoCard } from "../../components/TodoCard";
import { useLocation } from "wouter";
import { addDoc, collection, doc, onSnapshot, orderBy, query, updateDoc, where } from "firebase/firestore";
import { customerTypeOf, paths, ROLE_RANK, type Customer, type Estimate, type Invoice, type Job, type Project } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { useAuth } from "../../hooks/useAuth";
import { useLeadSources } from "../../hooks/useLeadSources";
import { useCustomerTypes } from "../../hooks/useCustomerTypes";
import { JobsCalendar } from "../../components/JobsCalendar";
import { call } from "../../lib/callFunction";
import { SendDocuments } from "../../components/SendDocuments";
import { PortalAccess } from "../../components/PortalAccess";
import { PortalMessages } from "../../components/PortalMessages";
import { ReferralCard } from "../../components/ReferralCard";
import { CustomerHistory } from "../../components/CustomerHistory";
import { SalesPage } from "../sales/SalesPage";
import { AddressField, locatedOf, type Located } from "../../components/AddressField";
import { CustomerAgreements } from "./AgreementsPage";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useAccess } from "../../hooks/useAccess";

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const fmtDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export function CustomerDetail({ customerId, onBack }: { customerId: string; onBack: () => void }) {
  const { tenantId, role } = useAuth();
  const [, navigate] = useLocation();
  const canManage = !!role && ROLE_RANK[role] >= ROLE_RANK.manager;
  const can = useAccess();
  const { hasFeature } = useEntitlements();
  const [customer, setCustomer] = useState<Customer | null>(null);
  // A builder's developments (Pulte Homes → Tobiano, …): one customer, many projects.
  const [projects, setProjects] = useState<Array<Project & { id: string }>>([]);
  const [newProject, setNewProject] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [jobLimit, setJobLimit] = useState(25);
  const [jobView, setJobView] = useState<"list" | "calendar">("list");
  const [jobs, setJobs] = useState<Array<Job & { id: string }>>([]);
  const [estimates, setEstimates] = useState<Array<Estimate & { id: string }>>([]);
  const [invoices, setInvoices] = useState<Array<Invoice & { id: string }>>([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ displayName: "", company: "", email: "", phone: "", addressLine1: "", city: "", state: "", zip: "", note: "", leadSource: "", type: "" });
  const leadSources = useLeadSources(tenantId);
  const customerTypes = useCustomerTypes(tenantId);
  const [loc, setLoc] = useState<Located>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(doc(db, paths.tenantCustomer(tenantId, customerId)), (snap) => {
        const data = (snap.data() as Customer) ?? null;
        setCustomer(data);
        if (data && !editing) {
          setForm({
            displayName: data.displayName,
            company: data.company,
            email: data.email,
            phone: data.phone,
            addressLine1: data.addressLine1,
            city: data.city,
            state: data.state,
            zip: data.zip,
            note: data.note,
            leadSource: data.leadSource ?? "",
            type: data.type ?? "",
          });
          setLoc(locatedOf(data));
        }
      }),
      onSnapshot(
        query(collection(db, paths.tenantJobs(tenantId)), where("customerId", "==", customerId), orderBy("createdAt", "desc")),
        (snap) => setJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) }))),
      ),
      onSnapshot(query(collection(db, paths.tenantProjects(tenantId)), where("customerId", "==", customerId)), (snap) =>
        setProjects(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Project) }))
            .sort((a, b) => Number(a.status !== "active") - Number(b.status !== "active") || a.name.localeCompare(b.name)),
        ),
      ),
      onSnapshot(
        query(collection(db, paths.tenantEstimates(tenantId)), where("customerId", "==", customerId), orderBy("createdAt", "desc")),
        (snap) => setEstimates(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Estimate) }))),
      ),
      onSnapshot(
        query(collection(db, paths.tenantInvoices(tenantId)), where("customerId", "==", customerId), orderBy("createdAt", "desc")),
        (snap) => setInvoices(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Invoice) }))),
      ),
    ];
    return () => subs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, customerId]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!tenantId) return;
    setBusy(true);
    try {
      const addressChanged = !!customer && (form.addressLine1 !== customer.addressLine1 || form.city !== customer.city || form.zip !== customer.zip);
      await updateDoc(doc(db, paths.tenantCustomer(tenantId, customerId)), {
        ...form,
        // Found or pinned: that spot. A changed address with no spot loses the old one.
        ...(loc ? { lat: loc.lat, lng: loc.lng } : addressChanged ? { lat: null, lng: null } : {}),
        updatedAt: Date.now(),
      });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  }

  async function addProject(e: FormEvent) {
    e.preventDefault();
    const name = newProject.replace(/\s+/g, " ").trim();
    if (!tenantId || !name) return;
    const project: Project = { name, customerId, status: "active", note: "", createdAt: Date.now(), updatedAt: Date.now() };
    await addDoc(collection(db, paths.tenantProjects(tenantId)), project);
    setNewProject("");
  }

  const jobCounts = useMemo(() => {
    const m = new Map<string, { open: number; total: number }>();
    for (const j of jobs) {
      const key = j.projectId ?? "";
      const c = m.get(key) ?? { open: 0, total: 0 };
      c.total += 1;
      if (j.status === "scheduled" || j.status === "in_progress") c.open += 1;
      m.set(key, c);
    }
    return m;
  }, [jobs]);
  const shownJobs = jobs.filter((j) => projectFilter === "all" || (j.projectId ?? "") === (projectFilter === "none" ? "" : projectFilter));

  if (!customer) return <p className="text-sm text-steel">Loading…</p>;

  const lifetimeBilled = invoices.filter((i) => i.status !== "void").reduce((s, i) => s + i.totalCents, 0);
  const lifetimePaid = invoices.reduce((s, i) => s + i.amountPaidCents, 0);
  const outstanding = lifetimeBilled - lifetimePaid;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <button className="text-xs text-steel hover:text-ink" onClick={onBack}>
            ← Customers
          </button>
          <h1 className="text-lg font-bold">{customer.displayName}</h1>
          <p className="text-xs text-steel">
            {customer.company ? `${customer.company} · ` : ""}
            {customer.phone || "no phone"}
            {customer.email ? ` · ${customer.email}` : ""}
          </p>
        </div>
        <div className="text-right text-xs text-steel">
          <div>
            Billed <span className="font-semibold text-ink">{money(lifetimeBilled)}</span>
          </div>
          {outstanding > 0 ? (
            <div>
              Outstanding <span className="font-semibold text-brandorange-dark">{money(outstanding)}</span>
            </div>
          ) : null}
        </div>
      </div>

      <Card>
        <div className="flex items-center justify-between">
          <CardTitle className="mb-0">Contact</CardTitle>
          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setEditing((v) => !v)}>
            {editing ? "Cancel" : "Edit"}
          </Button>
        </div>
        {editing ? (
          <form onSubmit={save} className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input required value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
            </div>
            <div>
              <Label>Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div>
              <Label>Lead source</Label>
              <Select value={form.leadSource} onChange={(e) => setForm({ ...form, leadSource: e.target.value })}>
                <option value="">Not asked</option>
                {[...new Set([...leadSources, ...(form.leadSource ? [form.leadSource] : [])])].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="">Not set ({customerTypeOf({ company: form.company }, projects.length > 0)} from the record)</option>
                {[...new Set([...customerTypes, ...(form.type ? [form.type] : [])])].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <AddressField
                fill="street"
                value={form.addressLine1}
                onChange={(addressLine1) => setForm((f) => ({ ...f, addressLine1 }))}
                located={loc}
                onLocate={setLoc}
                onPick={(m) => setForm((f) => ({ ...f, city: m.city || f.city, state: m.state || f.state, zip: m.zip || f.zip }))}
              />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>State</Label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
              </div>
              <div>
                <Label>Zip</Label>
                <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} />
              </div>
            </div>
            <div className="col-span-2">
              <Label>Internal note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Button type="submit" busy={busy}>
                Save
              </Button>
            </div>
          </form>
        ) : (
          <div className="mt-1 text-sm text-ink">
            {customer.addressLine1 ? (
              <p>
                {customer.addressLine1}
                {customer.city ? `, ${customer.city}` : ""} {customer.state} {customer.zip}
              </p>
            ) : (
              <p className="text-steel">No address on file.</p>
            )}
            {customer.note ? <p className="mt-1 text-xs text-steel">Note: {customer.note}</p> : null}
            <p className="mt-1 text-xs text-steel">
              {customerTypeOf(customer, projects.length > 0)}
              {customer.leadSource ? ` · Lead source: ${customer.leadSource}` : ""}
              {customer.mailchimp ? ` · In Mailchimp${customer.mailchimp.status ? ` (${customer.mailchimp.status})` : ""}` : ""}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-steel">Texts:</span>
              {customer.sms?.optedOutAt ? (
                <span className="text-danger">opted out (replied STOP); they can text START to turn texts back on</span>
              ) : (
                <Select
                  className="w-auto px-2 py-0.5 text-xs"
                  value={customer.sms?.consent ?? "unknown"}
                  onChange={(e) => void call("setCustomerTextConsent", { tenantId, customerId, consent: e.target.value, source: "phone" })}
                >
                  <option value="unknown">not asked yet</option>
                  <option value="yes">OK'd texts</option>
                  <option value="no">no texts, please</option>
                </Select>
              )}
            </div>
          </div>
        )}
      </Card>

      {projects.length > 0 || canManage ? (
        <Card className="p-0">
          <CardTitle className="px-4 pt-3">Projects ({projects.length})</CardTitle>
          {projects.map((p) => {
            const c = jobCounts.get(p.id) ?? { open: 0, total: 0 };
            return (
              <div key={p.id} className="flex items-center justify-between gap-2 border-b border-steel/10 px-4 py-1.5 text-sm">
                <button className="min-w-0 truncate text-left text-ink hover:text-brandorange" onClick={() => navigate(`/projects/${p.id}`)}>
                  {p.name}
                  {p.status !== "active" ? <span className="text-xs text-steel"> (closed)</span> : null}
                </button>
                <span className="flex shrink-0 items-center gap-2 text-xs text-steel">
                  {c.open} open · {c.total} job{c.total === 1 ? "" : "s"}
                  {c.total > 0 ? (
                    <button className="underline hover:text-ink" onClick={() => { setProjectFilter(p.id); setJobLimit(25); }}>
                      show jobs
                    </button>
                  ) : null}
                </span>
              </div>
            );
          })}
          {projects.length === 0 ? (
            <p className="px-4 py-1.5 text-xs text-steel">
              No projects yet. A builder's developments go here (one customer, many projects).
            </p>
          ) : null}
          {canManage ? (
            <form onSubmit={addProject} className="flex gap-2 px-4 py-2">
              <Input className="py-1 text-sm" placeholder="New project, e.g. Tobiano" value={newProject} onChange={(e) => setNewProject(e.target.value)} />
              <Button type="submit" variant="ghost" className="px-3 py-1 text-xs" disabled={!newProject.trim()}>
                + Add project
              </Button>
            </form>
          ) : null}
        </Card>
      ) : null}

      {jobs.length > 0 ? (
        <Card className="p-0">
          <div className="flex items-center justify-between gap-2 px-4 pt-3">
            <CardTitle className="mb-0">Jobs ({shownJobs.length}{shownJobs.length !== jobs.length ? ` of ${jobs.length}` : ""})</CardTitle>
            <div className="flex rounded-lg border border-steel/30 p-0.5 text-xs">
              {(["list", "calendar"] as const).map((v) => (
                <button key={v} className={cn("rounded-md px-2 py-0.5 capitalize", jobView === v ? "bg-ink text-white" : "text-steel")} onClick={() => setJobView(v)}>
                  {v}
                </button>
              ))}
            </div>
            {projects.length > 0 ? (
              <Select className="w-auto py-1 text-xs" aria-label="Project" value={projectFilter} onChange={(e) => { setProjectFilter(e.target.value); setJobLimit(25); }}>
                <option value="all">All projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                <option value="none">No project</option>
              </Select>
            ) : null}
          </div>
          {jobView === "calendar" ? (
            <div className="p-3">
              <JobsCalendar
                jobs={shownJobs.map((j) => ({
                  id: j.id,
                  title: j.title,
                  startAt: j.startAt,
                  status: j.status,
                  lot: j.lot ?? (j.projectId ? projects.find((p) => p.id === j.projectId)?.name : undefined),
                }))}
                onOpen={(id) => navigate(`/jobs/${id}`)}
              />
            </div>
          ) : null}
          <div className={cn("mt-2", jobView === "calendar" && "hidden")}>
            {shownJobs.slice(0, jobLimit).map((j) => (
              <button
                key={j.id}
                className="flex w-full items-center justify-between gap-2 border-b border-steel/10 px-4 py-1.5 text-left text-sm last:border-0 hover:bg-cream/60"
                onClick={() => navigate(`/jobs/${j.id}`)}
              >
                <span className="min-w-0 truncate text-ink">
                  {j.lot ? <strong>{j.lot} · </strong> : null}
                  {j.title}
                  {j.projectId && projectFilter === "all" ? (
                    <span className="text-xs text-steel"> · {projects.find((p) => p.id === j.projectId)?.name ?? ""}</span>
                  ) : null}
                </span>
                <span className="shrink-0 text-xs text-steel">
                  {j.startAt ? fmtDay(j.startAt) : "unscheduled"} · {j.status.replace("_", " ")}
                </span>
              </button>
            ))}
            {shownJobs.length > jobLimit ? (
              <button className="block w-full px-4 py-2 text-center text-xs text-steel hover:text-ink" onClick={() => setJobLimit((n) => n + 50)}>
                Show more
              </button>
            ) : null}
          </div>
        </Card>
      ) : null}

      {tenantId && can("customers.senddocs") ? (
        <SendDocuments tenantId={tenantId} customerId={customerId} suggestedEmails={customer.email ? [customer.email] : []} />
      ) : null}

      {tenantId && can("customers.portal") && hasFeature("customerPortal") ? (
        <>
          <PortalAccess tenantId={tenantId} customerId={customerId} defaultEmail={customer.email} defaultPhone={customer.phone} />
          <PortalMessages tenantId={tenantId} customerId={customerId} />
        </>
      ) : null}

      {tenantId && hasFeature("referrals") ? <ReferralCard tenantId={tenantId} customerId={customerId} customer={customer} /> : null}

      {tenantId && can("agreements") && hasFeature("serviceAgreements") ? <CustomerAgreements tenantId={tenantId} customerId={customerId} /> : null}

      {tenantId ? <TodoCard tenantId={tenantId} link={{ kind: "customer", id: customerId, label: (customer.displayName || "Customer").slice(0, 200) }} title="To-dos for this customer" /> : null}

      {/* The whole estimates and invoices lists for this customer, with every action. */}
      {tenantId && can("estimates") ? <SalesPage kind="estimate" customerId={customerId} customerName={customer.displayName} embedded /> : null}
      {tenantId && can("invoices") ? <SalesPage kind="invoice" customerId={customerId} customerName={customer.displayName} embedded /> : null}

      {tenantId ? <CustomerHistory tenantId={tenantId} customerId={customerId} jobs={jobs} estimates={estimates} invoices={invoices} /> : null}
    </div>
  );
}
